import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReAuth } from '@/services/http-client.factory';

/**
 * One RTK Query API for the whole courses module. Each `<domain>.api.ts` injects the
 * endpoints of the matching `<domain>.functions.ts` file of the original Lovable app.
 * Tags are per domain: a query provides its domain's tag, a mutation invalidates it
 * (plus the student-facing domains an admin change affects).
 */
type CoursesBaseQuery = typeof baseQueryWithReAuth;

/**
 * Query params with `null` values dropped: `fetchBaseQuery` would send them as the string
 * "null", which the API's validators reject. (`undefined` is already skipped.)
 */
const coursesBaseQuery: CoursesBaseQuery = (args, api, extraOptions) => {
	if (args && typeof args === 'object' && args.params) {
		const params = Object.fromEntries(
			Object.entries(args.params as Record<string, unknown>).filter(([, v]) => v !== null)
		);
		return baseQueryWithReAuth({ ...args, params }, api, extraOptions);
	}
	return baseQueryWithReAuth(args, api, extraOptions);
};

export const coursesApi = createApi({
	reducerPath: 'coursesApi',
	baseQuery: coursesBaseQuery,
	tagTypes: [
		'courses:access-requests',
		'courses:admin',
		'courses:admin-ama',
		'courses:admin-analytics',
		'courses:admin-challenges',
		'courses:admin-controls',
		'courses:admin-course-access',
		'courses:admin-courses',
		'courses:admin-emails',
		'courses:admin-grading',
		'courses:admin-modules',
		'courses:admin-office-hours',
		'courses:admin-user-profile',
		'courses:analytics-events',
		'courses:app-tags',
		'courses:challenges',
		'courses:community',
		'courses:courses',
		'courses:dashboard',
		'courses:discussions',
		'courses:event-feed',
		'courses:lesson-deeplink-logging',
		'courses:lessons',
		'courses:link-preview',
		'courses:modules',
		'courses:notes',
		'courses:office-hours',
		'courses:pdf-error-logging',
		'courses:tag-invites',
		'courses:user-tags',
		'courses:webinars',
	],
	endpoints: () => ({}),
});
